import React, { useState, useEffect } from "react";
import Image, { StaticImageData } from "next/image";
import styles from "./Slider.module.css";
import { Icon } from "@iconify/react";
import Button from "@/components/ui/button";
import { useExpedition } from "@/context/Expeditions";


interface HeroItem {
  _id: string;
  name: string;
  overview: string;
  height: string;
  banner: StaticImageData | string; // Updated to allow string from fetched data
  expeditionId?: string;
  collections?: { slug: string }; // Added for slug access
  slug?: string; // Added for navigation
  maxElevation?: string; // Added to match fetched data
}

const Slider: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleItems, setVisibleItems] = useState(5);
  const { heroExpeditions, error } = useExpedition();
  const [isLoading, setIsLoading] = useState(true);

  // Handle responsive thumbnail visibility
  useEffect(() => {
    const updateVisibleItems = () => {
      if (window.innerWidth >= 1280) {
        setVisibleItems(5);
      } else if (window.innerWidth >= 1024) {
        setVisibleItems(3);
      } else {
        setVisibleItems(0);
      }
    };

    updateVisibleItems();
    window.addEventListener("resize", updateVisibleItems);
    return () => window.removeEventListener("resize", updateVisibleItems);
  }, []);

  // Auto-slide interval
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % heroExpeditions.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [heroExpeditions.length]);

  // Loading state based on data availability
  useEffect(() => {
    if (heroExpeditions && heroExpeditions.length > 0) {
      setIsLoading(false); // Only set to false when data is ready
    }
  }, [heroExpeditions]);

  const handleClick = (index: number) => setActiveIndex(index);
  const handleNext = () =>
    setActiveIndex((prev) => (prev + 1) % heroExpeditions.length);
  const handlePrev = () =>
    setActiveIndex(
      (prev) => (prev - 1 + heroExpeditions.length) % heroExpeditions.length
    );

  // Show loading until data is fetched
  if (isLoading) {
    return (
      <div className="w-full h-screen relative bg-black">
        {/* Gradient overlay */}
        <div className="absolute top-0 left-0 w-full h-[20vh] z-10 bg-gradient-to-b from-black to-transparent"></div>
        
        {/* Main image skeleton with shimmer effect */}
        <div className="w-full h-full relative overflow-hidden bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-700/20 to-transparent animate-shimmer"></div>
        </div>

        {/* Content skeleton */}
        <div className="absolute left-[5%] 4xl:left-[9.4%] bottom-[15%] z-[1] max-w-5xl space-y-4">
          {/* Tags skeleton */}
          <div className="flex items-center gap-2">
            <div className="w-32 h-8 bg-zinc-800/80 rounded-full animate-pulse"></div>
            <div className="w-48 h-8 bg-zinc-800/80 rounded-full animate-pulse"></div>
          </div>

          {/* Title skeleton */}
          <div className="space-y-3">
            <div className="h-16 md:h-20 lg:h-24 bg-zinc-800/80 rounded-lg w-3/4 animate-pulse"></div>
            <div className="h-12 md:h-16 lg:h-20 bg-zinc-800/80 rounded-lg w-1/2 animate-pulse"></div>
          </div>

          {/* Buttons skeleton */}
          <div className="flex items-center gap-4 pt-4">
            <div className="w-36 h-12 bg-zinc-800/80 rounded-lg animate-pulse"></div>
            <div className="flex gap-2">
              <div className="w-12 h-12 bg-zinc-800/80 rounded-lg animate-pulse"></div>
              <div className="w-12 h-12 bg-zinc-800/80 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Thumbnail skeleton */}
        <div className="md:flex justify-end items-end hidden absolute bottom-[5%] right-[5%] 4xl:right-[9.4%] z-[11] gap-[10px]">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="w-[150px] h-[220px] bg-zinc-800/60 rounded-lg animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#1E1E1E] text-white">
        <p>Error loading expeditions: {error.message}</p>
      </div>
    );
  }

  return (
    <div
      className={styles.slider}
      style={{
        backgroundColor: "black",
      }}
    >
      <div className={styles.list}>
        {heroExpeditions.map((item, index) => (
          <div
            key={item._id}
            className={`${styles.item} ${index === activeIndex ? styles.active : ""
              }`}
          >
            <div className="absolute top-0 left-0 w-full h-[20vh] z-10 bg-gradient-to-b from-[#1E1E1E] to-transparent"></div>
            <Image
              src={item.banner}
              alt={`Slider ${index + 1}`}
              width={1280}
              height={720}
              objectFit="cover"
              quality={70}
              priority
            />
            <div
              className={`${styles.content} text-zinc-50 absolute left-[5%] 4xl:left-[9.4%] bottom-[15%] z-[1] max-w-5xl`}
            >
              <div className="flex items-center gap-2">
                <p className="w-fit px-4 py-1 rounded-full font-bold text-sm bg-orange-400/10 backdrop-blur-lg border border-orange-500 capitalize">
                  {item.collections?.slug === "trekking"
                    ? "Alpine Trekking"
                    : "Expedition"}
                </p>
                <p className="w-fit md:px-4 px-2 py-2 rounded-full font-bold md:text-sm text-xs bg-zinc-300 date text-green-600">
                  Booking Available Dates 2025-2026
                </p>
              </div>
              <div className="py-4 title font-bold text-[28px] lg:whitespace-nowrap xs:text-[40px] md:text-[60px] lg:text-[75px] 4xl:text-[110px] leading-tight flex flex-col">
                <p>{item.name}</p>
                <span className="text-[28px] xs:text-[30px] md:text-[60px] lg:text-[65px] 4xl:text-[100px]">
                  {item.maxElevation} M
                </span>
              </div>
              <div className="flex items-center gap-4">
                <a href={`/package_detail/${item.slug}`}>
                  <Button>Explore now</Button>
                </a>
                <div className={`${styles.arrows} relative flex left-2 gap-2`}>
                  <button
                    onClick={handlePrev}
                    className="flex justify-center items-center hover:bg-zinc-200 hover:scale-95 duration-200 !bg-zinc-200"
                  >
                    <Icon
                      icon="iconamoon:arrow-left-2-light"
                      className="w-7 h-7 object-cover object-center text-zinc-700"
                    />
                  </button>
                  <button
                    onClick={handleNext}
                    className="flex justify-center items-center hover:bg-zinc-200 hover:scale-95 duration-200 !bg-zinc-200"
                  >
                    <Icon
                      icon="iconamoon:arrow-right-2-light"
                      className="w-7 h-7 object-cover object-center text-zinc-700"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div
        className={`${styles.thumbnail} md:flex justify-end items-end hidden absolute bottom-[5%] right-[5%] 4xl:right-[9.4%] z-[11] gap-[10px] w-[50%] box-border`}
      >
        {heroExpeditions.slice(0, visibleItems).map((item, index) => (
          <div
            key={item._id}
            className={`${styles.item} ${index === activeIndex ? `${styles.active}` : ""
              } cursor-pointer`}
            onClick={() => handleClick(index)}
          >
            <Image
              width={1920}
              height={1080}
              src={item.banner}
              alt={`Thumbnail ${index + 1}`}
              objectFit="cover"
              className="brightness-50"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Slider;
